// Optimized games.js with proper dynamic imports

// Keep critical above-the-fold images as static imports
import pokemonBg from '../pokemonbg.webp';
import blackBoltWhiteFlareLogo from '../bb-wf-logo.webp';
import megaEvolutionLogo from '../mega_evolution.webp';
import eoeLogo from '../eoe-logo.webp';
import onePieceLogo from '../one_piece_tcg_logo.webp';
import magicBg from '../magicbg.webp';
import onePieceBg from '../onepiecebg.webp';
import spiderman from '../spiderman.png';
import soulstone from '../cards/spiderman.webp';

// New bgs
import oBg from '../o-bg.webp';
import mBg from '../m-bg.webp';
import pBg from '../p-bg.webp';

// Static imports for critical stock images
import bbbg from '../bbbg.webp';
import tezzeret from '../cards/tezzeret.webp';
import mewtwo from '../cards/mewtwo.webp';
import reshiram from '../cards/reshiram.webp';
import umbreon from '../cards/umbreon.webp';
import lucario from '../cards/lucario.webp';
import drbg from '../drbg.webp';
import eoebg from '../eoe-bg.webp';
import pebg from '../pebg.webp';

export const GAME_DETAILS = {
  pokemon: {
    name: 'Pokémon TCG',
    shortName: 'Pokémon',
    color: 'text-blue-200',
    bgCol: '#210f03',
    image: pBg, // Keep critical hero image static
    bgSpeedLinesHueRot: '330deg',

    // Use string paths for dynamic loading
    packs: [
      ['../games/pokemon/packs/151.webp', '151'],
      ['../games/pokemon/packs/destined_rivals.webp', 'Destined Rivals'],
      ['../games/pokemon/packs/mega_evolution.webp', 'Mega Evolution'],
      ['../games/pokemon/packs/black_bolt.webp', 'Black Bolt & White Flare'],
    ],

    logo: megaEvolutionLogo, // Keep above-the-fold static
    underLogoText: 'Available now!',

    packsInStore: new Map([
      [
        'Mega Evolution',
        {
          cardCount: 180,
          name: 'Mega Evolution',
          bg: '#b3bd2d',
          bgImage: pebg,
          year: '2025',
          langs: 'EN and JP',
          description: (
            <div className="stock-description">
              <strong>Mega Evolution is here!</strong>
              <p>
                Mega Evolutions make their triumphant return with over 180
                cards, including the glorious Mega Lucario ex.
              </p>
              <p>
                Available in both English and Japanese, with booster packs,
                ETBs, and singles in stock now!
              </p>
            </div>
          ),
          card: lucario,
          cardName: 'Mega Lucario ex',
          rarity: 'Mega Hyper Rare',
          default: false,
        },
      ],
      [
        "Marvel's Spider-Man",
        {
          cardCount: '190',
          name: "Marvel's Spider-Man",
          bg: '#cc004e',
          bgImage: eoebg,
          year: '2025',
          langs: 'EN',
          description: (
            <div className="stock-description">
              <strong>
                MTG's collaboration with Marvel's Spider-Man is in stock!
              </strong>
              <p>
                Swing into action with over 190 cards featuring your favorite
                web-slinger. Includes iconic characters such as Miles Morales,
                Peter Parker, and Gwen Stacy.
              </p>
              <p>In store now!</p>
            </div>
          ),
          card: soulstone,
          cardName: 'Spectacular Spider-Man',
          rarity: 'Rare',
        },
      ],
      [
        'Edge of Eternities',
        {
          cardCount: '300',
          name: 'Edge of Eternities',
          bg: '#8100cc',
          bgImage: eoebg,
          year: '2025',
          langs: 'EN',
          description: (
            <div className="stock-description">
              <strong>Edge of Eternities is here!</strong>
              <p>
                Venture through Sothera with Magic's new space-fantasy set. Edge
                of Eternities features over 300 cards, including the powerful
                Tezzeret, Cruel Captain.
              </p>
              <p>Play boosters in store now!</p>
            </div>
          ),
          card: tezzeret,
          cardName: 'Tezzeret, Cruel Captain',
          rarity: 'Mythic Rare',
        },
      ],
      [
        'Black Bolt & White Flare',
        {
          cardCount: '340',
          name: 'Black Bolt and White Flare',
          bg: '#a8006d',
          bgImage: bbbg, // Keep stock images static for now
          year: '2025',
          langs: 'EN and JP',
          description: (
            <div className="stock-description">
              <strong>English Black Bolt & White Flare available now!</strong>
              <p>
                Take a trip down memory lane back to Unova, and get your hands
                on the coveted Reshiram ex and Zekrom ex.
              </p>
              <p>Boosters, ETBs, tins, and Binder Collections available now.</p>
            </div>
          ),
          card: reshiram,
          cardName: 'Reshiram ex',
          rarity: 'Black White Rare',
          default: true,
          closedPack: '../games/pokemon/packs/black_bolt.webp',
          openPack: '../games/pokemon/packs/151.webp',
        },
      ],
    ]),

    buttonColours: {
      gradient: ['rose', 'cyan', 'yellow'],
      border: '#FFFFFF',
      accent: '',
    },
  },

  magic: {
    name: 'Magic: The Gathering',
    shortName: 'MTG',
    color: 'text-red-200',
    image: mBg, // Keep critical images static
    bgCol: '#030421',
    packs: [
      ['../games/mtg/packs/tarkir_dragonstorm.webp', 'Tarkir Dragonstorm'],
      ['../games/mtg/packs/ff.webp', 'Final Fantasy'],
      ['../games/mtg/packs/spiderman.webp', "Marvel's Spider-Man"],
      ['../games/mtg/packs/eoe.webp', 'Edge of Eternities'],
    ],
    logo: spiderman, // Keep logo static for above-the-fold
    bgSpeedLinesHueRot: '-70deg',
    packsInStore: ['Secret Lair', 'Final Fantasy', 'Bloomburrow'],
    buttonColours: {
      gradient: ['blue', 'red', 'fuchsia'],
      border: '#FFFFFF',
      accent: '',
    },
    underLogoText: 'Available now!',
  },

  onepiece: {
    name: 'One Piece',
    color: 'text-purple-200',
    image: oBg, // Keep critical images static
    bgCol: '#150321',
    packs: [
      ['../games/one-piece/packs/500.webp', 'OP-05'],
      ['../games/one-piece/packs/two_legends.webp', 'Two Legends'],
      ['../games/one-piece/packs/emperors.webp', 'Emperors'],
      ['../games/one-piece/packs/wings.webp', 'Wings'],
    ],
    logo: onePieceLogo, // Keep logo static for above-the-fold
    bgSpeedLinesHueRot: '180deg',
    packsInStore: ['Wings of the Captain', 'Two Legends', 'Romance Dawn'],
    buttonColours: {
      gradient: ['cyan', 'purple', 'indigo'],
      border: '#FFFFFF',
      accent: '',
    },
  },
};
